import { useState, useEffect, useContext, useCallback } from "react";
import { API, graphqlOperation } from "aws-amplify";
import {
  Button,
  Fab,
  Divider,
  Box,
  IconButton,
  Typography,
  FormControl,
  TextField,
  InputAdornment,
  Chip,
  Grid,
  Card,
  CardContent,
  CardActions,
  Stack,
  Alert,
  AlertTitle,
  CircularProgress,
  MenuItem,
  Select,
  InputLabel,
  LinearProgress,
  Skeleton,
  Drawer,
} from "@mui/material";
import NavigationIcon from "@mui/icons-material/Navigation";
import SearchIcon from "@mui/icons-material/Search";
import InfoIcon from "@mui/icons-material/Info";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import GridViewIcon from "@mui/icons-material/GridView";
import TableRowsIcon from "@mui/icons-material/TableRows";
import CloseIcon from "@mui/icons-material/Close";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { useTheme } from "@mui/material/styles";
import { AppContext } from "../App";
import { UserCard } from "./shared";
import {
  fetchUsers,
  fetchUserByEmail,
  fetchCustomersForProvider,
} from "../services/userManagementService";
import { useUser } from "../UserContext";
import CreateUserForm from "./CreateUserForm";
import { listUserGroups, listPricelists, listUsers } from "../graphql/queries";
import UserDialog from "./shared/UserDialog";
import PricelistSelect from "./pricing/PricelistSelect";
import InnerPage from "./InnerPage";

// Utility function to format relative time
const getRelativeTime = (dateString) => {
  if (!dateString) return { text: "Never", status: "never" };

  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) {
    return { text: "Just now", status: "recent" };
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return {
      text: `${diffInMinutes} minute${diffInMinutes > 1 ? "s" : ""} ago`,
      status: diffInMinutes < 30 ? "recent" : "warning",
    };
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return {
      text: `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`,
      status: diffInHours < 12 ? "recent" : "warning",
    };
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return {
      text: `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`,
      status: diffInDays < 3 ? "warning" : "old",
    };
  }

  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 4) {
    return {
      text: `${diffInWeeks} week${diffInWeeks > 1 ? "s" : ""} ago`,
      status: "old",
    };
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    return {
      text: `${diffInMonths} month${diffInMonths > 1 ? "s" : ""} ago`,
      status: "old",
    };
  }

  return { text: date.toLocaleDateString(), status: "old" };
};

const Users = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [userGroups, setUserGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [viewMode, setViewMode] = useState("card"); // 'card' or 'row'
  const theme = useTheme();
  const { userRecord } = useContext(AppContext);
  const { activeProvider } = useUser();

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setSearchLoading(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  // Custom resolver-like function that filters users by provider
  const listUsersByProvider = async (providerRecord, limit) => {
    try {
      console.log(
        "[listUsersByProvider] Called with providerRecord.id:",
        providerRecord.id,
        "limit:",
        limit,
      );

      // Use the consolidated fetchCustomersForProvider function from userManagementService
      // Pass the full userRecord object, not just the ID
      const customers = await fetchCustomersForProvider(providerRecord, limit);

      console.log("[listUsersByProvider] Fetched customers:", customers.length);
      if (customers.length > 0) {
        console.log("[listUsersByProvider] Sample customer:", customers[0]);
      }

      return customers;
    } catch (error) {
      console.error("[listUsersByProvider] Error:", error);
      throw error;
    }
  };

  const applyFilters = useCallback(
    (userList) => {
      let filtered = [...userList];

      if (debouncedSearch) {
        filtered = filtered.filter((user) =>
          Object.values(user).some((value) =>
            value
              ?.toString()
              .toLowerCase()
              .includes(debouncedSearch.toLowerCase()),
          ),
        );
      }

      if (selectedGroup) {
        filtered = filtered.filter(
          (user) => user.userGroupID === selectedGroup,
        );
      }

      setFilteredUsers(filtered);
    },
    [debouncedSearch, selectedGroup],
  );

  const resetUsers = useCallback(() => {
    setUsers([]);
    setFilteredUsers([]);
  }, []);

  useEffect(() => {
    console.log(
      "[applyFilters effect] Called - users:",
      users.length,
      "debouncedSearch:",
      debouncedSearch,
      "selectedGroup:",
      selectedGroup,
    );
    applyFilters(users);
  }, [debouncedSearch, selectedGroup, users, applyFilters]);

  const handleViewEdit = async (user) => {
    try {
      const fullUserDetails = await fetchUserByEmail(user.email);

      if (!fullUserDetails) {
        console.error("No user details returned for", user.email);
        return;
      }

      setSelectedUser(fullUserDetails);
      setDialogOpen(true);
    } catch (error) {
      console.error("Error fetching user details:", error);
    }
  };

  const handleUserCreated = () => {
    setShowCreateForm(false);
    resetUsers();
  };

  const handleUserUpdated = () => {
    setDialogOpen(false);
    setSelectedUser(null);
    resetUsers();
  };

  // Fetch user groups
  useEffect(() => {
    const fetchUserGroups = async () => {
      console.log("[fetchUserGroups] Called, userRecord:", userRecord);
      if (!userRecord?.id) return;

      try {
        console.log(
          "[fetchUserGroups] Fetching user groups for userRecord.id:",
          userRecord.id,
        );
        const result = await API.graphql(
          graphqlOperation(listUserGroups, {
            filter: { userID: { eq: userRecord.id } },
          }),
        );

        const groups = result.data.listUserGroups?.items || [];
        console.log("[fetchUserGroups] Fetched groups:", groups.length);

        const groupsWithNames = groups.map((g) => ({
          GroupName: g.title,
          id: g.id,
        }));
        const sortedGroups = groupsWithNames.sort((a, b) =>
          a.GroupName.localeCompare(b.GroupName),
        );
        setUserGroups(sortedGroups);
      } catch (error) {
        console.error("[fetchUserGroups] Error:", error);
        setUserGroups([]);
      }
    };

    fetchUserGroups();
  }, [userRecord]);

  // Load users when userRecord changes
  useEffect(() => {
    const fetchUsers = async () => {
      if (!userRecord?.id) {
        setUsers([]);
        setFilteredUsers([]);
        return;
      }

      setLoading(true);
      try {
        const customers = await listUsersByProvider(userRecord, 200);
        setUsers(customers || []);
      } catch (error) {
        console.error("[fetchUsers] Error:", error);
        setUsers([]);
        setFilteredUsers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [userRecord?.id]);

  return (
    <Box>
      <Divider />

      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: "text.primary",
            mb: 1,
          }}
        >
          Customer Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage and monitor your customer accounts
        </Typography>
      </Box>

      {/* Search and Filter Section */}
      <Box
        sx={{
          mb: 4,
          p: 3,
          backgroundColor: "background.paper",
          borderRadius: 3,
          boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
        }}
      >
        <Grid container spacing={2} alignItems="center">
          {/* Search Field */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search by name, email, or any field..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setSearchLoading(true);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchLoading ? (
                  <InputAdornment position="end">
                    <CircularProgress size={20} />
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />
          </Grid>

          {/* Group Filter */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Filter by User Group</InputLabel>
              <Select
                value={selectedGroup}
                label="Filter by User Group"
                onChange={(e) => setSelectedGroup(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">All Groups</MenuItem>
                {userGroups.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.GroupName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Active Filters Display */}
        {(search || selectedGroup) && (
          <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
            {search && (
              <Chip
                label={`Search: "${search}"`}
                onDelete={() => {
                  setSearch("");
                  setDebouncedSearch("");
                }}
                color="primary"
                variant="outlined"
              />
            )}
            {selectedGroup && (
              <Chip
                label={`Group: ${
                  userGroups.find((g) => g.id === selectedGroup)?.GroupName
                }`}
                onDelete={() => setSelectedGroup("")}
                color="secondary"
                variant="outlined"
              />
            )}
          </Box>
        )}
      </Box>

      {/* Info Alert */}
      <Alert
        severity="info"
        icon={<InfoIcon />}
        sx={{
          mb: 3,
          borderRadius: 2,
          "& .MuiAlert-message": {
            width: "100%",
          },
        }}
      >
        <AlertTitle sx={{ fontWeight: 700 }}>About User Groups</AlertTitle>
        <Typography variant="body2" paragraph>
          User Groups are powerful access control tools that determine what
          features and pricelists customers can access.
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: "info.dark" }}
        >
          ⚠️ Important: When a customer is assigned to a User Group, the group's
          pricelist settings will override their individually assigned
          pricelist.
        </Typography>
      </Alert>

      {/* Results Count */}
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
        <Typography variant="body1" color="text.secondary">
          Showing <strong>{filteredUsers.length}</strong> customers
        </Typography>
        {loading && <LinearProgress sx={{ flex: 1, borderRadius: 1 }} />}
        <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>
          <IconButton
            onClick={() => setViewMode("card")}
            color={viewMode === "card" ? "primary" : "default"}
            size="small"
          >
            <GridViewIcon />
          </IconButton>
          <IconButton
            onClick={() => setViewMode("row")}
            color={viewMode === "row" ? "primary" : "default"}
            size="small"
          >
            <TableRowsIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Loading State */}
      {loading ? (
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid item xs={12} sm={6} lg={4} key={i}>
              <Card sx={{ height: "100%", borderRadius: 3 }}>
                <Box sx={{ p: 2, background: "#e0e0e0" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Skeleton variant="circular" width={56} height={56} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="60%" height={28} />
                      <Skeleton variant="text" width="40%" />
                    </Box>
                  </Box>
                </Box>
                <CardContent>
                  <Stack spacing={2}>
                    <Skeleton variant="text" />
                    <Skeleton variant="text" />
                    <Skeleton variant="text" />
                    <Skeleton variant="text" />
                    <Skeleton variant="text" />
                  </Stack>
                </CardContent>
                <CardActions>
                  <Skeleton
                    variant="rectangular"
                    width="45%"
                    height={36}
                    sx={{ borderRadius: 1 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    width="45%"
                    height={36}
                    sx={{ borderRadius: 1 }}
                  />
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : filteredUsers.length === 0 && !loading ? (
        // Empty State
        <Box
          sx={{
            textAlign: "center",
            py: 8,
            px: 2,
            backgroundColor: "background.paper",
            borderRadius: 3,
            border: "2px dashed",
            borderColor: "divider",
          }}
        >
          <SearchOffIcon
            sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
          />
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            {search || selectedGroup
              ? "No customers found"
              : "No customers yet"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {search || selectedGroup
              ? "Try adjusting your search or filters"
              : "Start by adding your first customer"}
          </Typography>
          {(search || selectedGroup) && (
            <Button
              variant="outlined"
              onClick={() => {
                setSearch("");
                setDebouncedSearch("");
                setSelectedGroup("");
              }}
            >
              Clear Filters
            </Button>
          )}
        </Box>
      ) : viewMode === "card" ? (
        // User Cards Grid
        <Grid container spacing={3}>
          {filteredUsers.map((user) => (
            <Grid item xs={12} sm={6} lg={4} key={user.email}>
              <UserCard
                user={user}
                onViewEdit={handleViewEdit}
                currentUserID={userRecord?.id}
                onPricelistUpdate={resetUsers}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        // User Table View
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell>Group</TableCell>
                <TableCell>Pricelist</TableCell>
                <TableCell>Offset</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((user) => {
                const lastLogin = getRelativeTime(user.lastLogin);
                // Determine offset display
                const offsetValue =
                  user.priceOffsetValue !== null &&
                  user.priceOffsetValue !== undefined
                    ? user.priceOffsetValue
                    : 0;
                const offsetType = user.priceOffsetType || "percentage";

                return (
                  <TableRow key={user.id}>
                    <TableCell>{user.username || "Unknown User"}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{lastLogin.text}</TableCell>
                    <TableCell>{user.userGroupID || "Unassigned"}</TableCell>
                    <TableCell align="right">
                      <PricelistSelect
                        pricelistID={user.activePricelistID}
                        userID={user.id}
                        providerID={user.createdByUserId || userRecord?.id}
                        onPricelistUpdate={resetUsers}
                      />
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        {/* Customer's Personal Offset */}
                        <Chip
                          label={
                            offsetValue !== 0
                              ? offsetType === "percentage"
                                ? `${offsetValue > 0 ? "+" : ""}${offsetValue}%`
                                : `$${offsetValue > 0 ? "+" : ""}${Math.abs(
                                    offsetValue,
                                  ).toFixed(2)}`
                              : "No offset"
                          }
                          size="small"
                          color={
                            offsetValue > 0
                              ? "warning"
                              : offsetValue < 0
                                ? "success"
                                : "default"
                          }
                          sx={{ fontWeight: "bold", width: "fit-content" }}
                        />
                        {/* Provider's Personal Offset */}
                        <Chip
                          label={
                            user.providerOffsetValue &&
                            user.providerOffsetValue !== 0
                              ? user.providerOffsetType === "percentage"
                                ? `${user.providerOffsetValue > 0 ? "+" : ""}${user.providerOffsetValue}%`
                                : `$${user.providerOffsetValue > 0 ? "+" : ""}${Math.abs(
                                    user.providerOffsetValue,
                                  ).toFixed(2)}`
                              : "No offset"
                          }
                          size="small"
                          color={
                            user.providerOffsetValue &&
                            user.providerOffsetValue > 0
                              ? "warning"
                              : user.providerOffsetValue &&
                                  user.providerOffsetValue < 0
                                ? "success"
                                : "default"
                          }
                          sx={{ fontWeight: "bold", width: "fit-content" }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleViewEdit(user)}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Fab
        variant="extended"
        color="secondary"
        onClick={() => setShowCreateForm(true)}
        sx={{
          position: "fixed",
          bottom: 32,
          right: 32,
          borderRadius: 3,
          boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
          "&:hover": {
            boxShadow: "0 6px 24px rgba(0,0,0,0.3)",
          },
        }}
      >
        <NavigationIcon sx={{ mr: 1 }} />
        Create User
      </Fab>

      {/* Create User Drawer */}
      <Drawer
        anchor="right"
        open={showCreateForm}
        onClose={() => setShowCreateForm(false)}
      >
        <Box
          sx={{
            width: 450,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              p: 3,
              pb: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid",
              borderColor: "divider",
              flexShrink: 0,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Create New Customer
            </Typography>
            <IconButton onClick={() => setShowCreateForm(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              p: 3,
            }}
          >
            <CreateUserForm onUserCreated={handleUserCreated} />
          </Box>
        </Box>
      </Drawer>

      {/* View/Edit User Dialog */}
      {selectedUser && (
        <UserDialog
          user={selectedUser}
          open={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
            setSelectedUser(null);
          }}
          onEditUser={handleUserUpdated}
        />
      )}
    </Box>
  );
};

export default Users;
